import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'designSystem/components/DropdownMenu'
import { useAdvancedSearchUX } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/AdvancedSearchUXContext'
import { useResolvedDocChatModelSettings } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useResolvedDocChatModelSettings'
import { coerceThinkingLevel } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatModels'
import { PermissionsGate } from 'features/permissions'
import { ChevronDown } from 'lucide-react'
import React from 'react'
import { PERMISSION_GECKO_INTERNAL_MEMBER } from 'types'

const THINKING_LEVEL_LABELS = new Map<string, MessageDescriptor>([
  ['MINIMAL', msg`Minimal`],
  ['LOW', msg`Low`],
  ['MEDIUM', msg`Medium`],
  ['HIGH', msg`High`],
])

export function DocChatModelSettingsMenu(): React.ReactElement | null {
  const { siteId } = useAdvancedSearchUX()
  const {
    options,
    model,
    thinkingLevel,
    loaded,
    isInternal,
    setModel,
    setThinkingLevel,
  } = useResolvedDocChatModelSettings(siteId)
  const { t, i18n } = useLingui()

  const activeModel =
    options.models.find((m) => m.model === model) ?? options.models[0]

  // Wait for the backend capabilities before rendering: the built-in fallback is
  // the GCP Gemini list, so showing it on an Azure deployment would let a user
  // pick a model the backend rejects.
  if (!isInternal || !loaded) return null

  return (
    <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant='outline'
            shape='round'
            size='default'
            type='button'
            title={t`Model settings`}>
            <Trans>Model</Trans>
            <ChevronDown aria-hidden data-icon='inline-end' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='start' side='top'>
          <DropdownMenuLabel>
            <Trans>Model</Trans>
          </DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={activeModel.model}
            onValueChange={(value) => {
              const next = options.models.find((m) => m.model === value)
              setModel(value)
              if (next) {
                setThinkingLevel(coerceThinkingLevel(next, thinkingLevel))
              }
            }}>
            {options.models.map((option) => (
              <DropdownMenuRadioItem key={option.model} value={option.model}>
                {option.model}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>
            <Trans>Reasoning effort</Trans>
          </DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={thinkingLevel}
            onValueChange={(value) => {
              setThinkingLevel(value)
            }}>
            {activeModel.thinkingLevels.map((level) => {
              const label = THINKING_LEVEL_LABELS.get(level)
              return (
                <DropdownMenuRadioItem key={level} value={level}>
                  {label ? i18n._(label) : level}
                </DropdownMenuRadioItem>
              )
            })}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </PermissionsGate>
  )
}
